import React from "react";
import "./Datatable.scss";
import { useTable, useBlockLayout, useResizeColumns } from "react-table";

const DataTable = (props) => {
  const data = React.useMemo(() => props.data, [props.data]);
  const columns = React.useMemo(() => props.columns, [props.columns]);
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 400,
    }),
    []
  );

  const tableConfig = {
    columns,
    data,
    defaultColumn,
  };

  if (data.length > 0) {
    const firstRowId = data[0]?.id;
    if (firstRowId !== undefined && (typeof firstRowId === 'string' || typeof firstRowId === 'number')) {
      tableConfig.getRowId = (row) => {
        return String(row.id);
      };
    }
  }

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      tableConfig,
      useBlockLayout,
      useResizeColumns
    );

  return (
    <>
      <table className="table" {...getTableProps()}>
        <thead style={{ position: "sticky", top: 0, zIndex: 11 }}>
          {headerGroups.map((headerGroup) => (
            <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => {
                const isSticky = column.isSticky;
                const isStickyRight = column.isStickyRight;

                const stickyStyle = isSticky
                  ? {
                    position: "sticky",
                    left: headerGroup.headers
                      .slice(0, index)
                      .reduce((acc, curr) => (curr.isSticky ? acc + curr.width : acc), 0),
                    zIndex: 10,
                    backgroundColor: "#fff",
                  }
                  : isStickyRight
                    ? {
                      position: "sticky",
                      right: headerGroup.headers
                        .slice(index + 1)
                        .reduce((acc, curr) => (curr.isStickyRight ? acc + curr.width : acc), 0),
                      zIndex: 10,
                      backgroundColor: "#fff",
                    }
                    : {
                      backgroundColor: "#fff",
                      position: "relative",
                      zIndex: 1,
                    };

                const headerWidth = column.totalWidth || column.width;

                return (
                  <th
                    key={column.id}
                    {...column.getHeaderProps()}
                    className={column.id ? `th-${column.id}` : undefined}
                    style={{
                      ...stickyStyle,
                      width: headerWidth,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }}
                  >
                    {column.render("Header")}
                    {(column.Header || index === 0) && (
                      <div
                        {...column.getResizerProps()}
                        className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const customRowProps = props.getRowProps ? props.getRowProps(row) : {};
            const rowHighlightStyle = customRowProps.style || {};
            const hasHighlight = !!rowHighlightStyle.borderLeft || !!rowHighlightStyle.boxShadow;
            const baseRowProps = row.getRowProps();
            const mergedRowStyle = { ...baseRowProps.style, ...(customRowProps.style || {}) };
            const mergedRowClassName = [baseRowProps.className, customRowProps.className]
              .filter(Boolean)
              .join(" ");
            const leafHeaders = headerGroups[headerGroups.length - 1].headers;
            return (
              <tr
                key={row.id}
                {...baseRowProps}
                style={mergedRowStyle}
                className={mergedRowClassName || undefined}
              >
                {row.cells.map((cell, index) => {
                  const isSticky = cell.column.isSticky;
                  const isStickyRight = cell.column.isStickyRight;

                  const stickyCellStyle = isSticky
                    ? {
                      position: "sticky",
                      left: leafHeaders
                        .slice(0, index)
                        .reduce((acc, curr) => (curr.isSticky ? acc + curr.width : acc), 0),
                      zIndex: 10,
                    }
                    : isStickyRight
                      ? {
                        position: "sticky",
                        right: leafHeaders
                          .slice(index + 1)
                          .reduce((acc, curr) => (curr.isStickyRight ? acc + curr.width : acc), 0),
                        zIndex: 10,
                      }
                      : {};

                  const cellStyle = {
                    ...stickyCellStyle,
                    width: cell.column.width,
                    minWidth: cell.column.minWidth,
                    maxWidth: cell.column.maxWidth,
                  };

                  if (cell.column.id === 'project') {
                    cellStyle.justifyContent = 'flex-start';
                    cellStyle.textAlign = 'left';
                  }

                  if (index === 0 && hasHighlight && !isSticky) {
                    cellStyle.position = 'relative';
                  }

                  const baseClassName = `col-${cell.column.id}`;
                  const highlightClassName = index === 0 && hasHighlight ? 'highlighted-active' : '';

                  return (
                    <td
                      key={cell.row.id}
                      {...cell.getCellProps()}
                      style={cellStyle}
                      className={`${baseClassName} ${highlightClassName}`.trim()}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default DataTable;
